import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { useAuth } from '../../api/auth';
import { Alert, Button, Field, FullPageLoader, Logo } from '../../components/ui';

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  if (loading) return <FullPageLoader />;
  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.details ?? {});
      } else {
        setError('სერვერთან კავშირი ვერ დამყარდა');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          <img src="/step-logo.png" alt="STEP IT Academy" className="size-16 object-contain" />
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-ink">IT Academy Step</h1>
            <p className="text-[13px] font-medium uppercase tracking-wider text-ink-mute">LMS</p>
          </div>
        </div>

        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="space-y-4 rounded-[10px] border border-line bg-surface p-6 shadow-xs"
        >
          {error && <Alert kind="error">{error}</Alert>}

          <Field
            label="ელფოსტა"
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={fieldErrors.email}
            placeholder="name@itstep.org"
          />

          <Field
            label="პაროლი"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={fieldErrors.password}
            placeholder="••••••••"
          />

          <Button type="submit" loading={busy} className="w-full">
            შესვლა
          </Button>

          <Link
            to="/forgot-password"
            className="block text-center text-[13px] text-brand-700 hover:underline"
          >
            დაგავიწყდა პაროლი?
          </Link>

          <p className="pt-1 text-center text-[12px] leading-relaxed text-ink-mute">
            ანგარიშს ქმნის ადმინისტრატორი ან მენეჯერი.
            <br />
            მიღებული ბმულით დააყენე პაროლი.
          </p>
        </form>

        <div className="mt-6 flex justify-center opacity-60">
          <Logo showText={false} />
        </div>
      </div>
    </div>
  );
}
